import { useState } from "react";
import { MatterType } from "../../../models/MatterType";
import { useMatterType } from "../../../contexts/MatterTypeContext";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { Switch, SwitchField } from "../../../design/switch";
import { storage } from "../../../firebase";
import { getSubdomainFromUrl } from "../../../utils/url";
import { v4 as uuidv4 } from "uuid";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

interface Props {
  matterType: MatterType;
}

interface Errors {
  name?: string;
  description?: string;
}

const EditMatterTypeButton: React.FC<Props> = ({ matterType }) => {
  const { updateMatterType } = useMatterType();
  const [photo, setPhoto] = useState<File | null>(null);
  const [name, setName] = useState(matterType.name);
  const [description, setDescription] = useState(matterType.description);
  const [qualifications, setQualifications] = useState(
    matterType.qualifications,
  );
  const [isPrivate, setIsPrivate] = useState(matterType.isPrivate);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name) {
      newErrors.name = "Name is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      await updateMatterType(matterType._id, photo, {
        name,
        description,
        qualifications,
        isPrivate,
      });
      clearForm();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setName(matterType.name);
    setDescription(matterType.description);
    setQualifications(matterType.qualifications);
    setErrors({});
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Matter Type</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setPhoto(file);
                    }
                  }}
                />
              </Field>

              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>

              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  rows={5}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </Field>

              <Field>
                <Label>Qualifications</Label>
                <Textarea
                  value={qualifications}
                  rows={5}
                  onChange={(e) => setQualifications(e.target.value)}
                  placeholder="Qualifications"
                />
              </Field>

              <SwitchField>
                <Label>Is Private?</Label>
                <Description>
                  Clients are not able to select this matter unless they are
                  given a direct link.
                </Description>
                <Switch
                  defaultChecked={isPrivate}
                  onChange={(checked) => setIsPrivate(checked)}
                />
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditMatterTypeButton;
